import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	ButtonProps,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	ModalProps,
	RequiredKeys,
	Section,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Table } from "@hex-insights/tables";
import {
	ContractLineItem,
	useChildContractLineItemListQuery,
	useContractLineItemDetailQuery,
} from "../../../../Utilities";
import { ContractLineItemDeleteButton } from "../../../DeleteButtons";
import { ContractLineItemFormat } from "../../../Format";
import { ContractLineItemForm } from "../../../Forms";

export type ControlledContractLineItemDetailModalControllerProps = Omit<ButtonProps, "onClick"> &
	Pick<ControlledContractLineItemDetailModalProps, "contractLineItemID" | "formStateArgs" | "formNameSuffix">;

export function ControlledContractLineItemDetailModalController({
	contractLineItemID,
	formStateArgs,
	formNameSuffix,
	...props
}: ControlledContractLineItemDetailModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} {...props} />

			<Modal.If condition={isModalOpen}>
				<ControlledContractLineItemDetailModal
					contractLineItemID={contractLineItemID}
					formStateArgs={formStateArgs}
					formNameSuffix={formNameSuffix}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

export type ControlledContractLineItemDetailModalProps = Pick<
	ContractLineItemForm.ControlledDetailProps,
	"formStateArgs" | "formNameSuffix"
> & {
	contractLineItemID: ContractLineItem["id"];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

export function ControlledContractLineItemDetailModal({
	contractLineItemID,
	formStateArgs,
	formNameSuffix,
	ifRef,
	onClose,
	className,
	style,
}: ControlledContractLineItemDetailModalProps) {
	const { data } = useContractLineItemDetailQuery({ variables: { id: contractLineItemID } });
	const contractLineItem = data?.contractLineItem;

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={className} style={style}>
			{!!contractLineItem && (
				<Modal.Header>
					<Heading.H2 noMargin>{ContractLineItemFormat.name(contractLineItem)}</Heading.H2>
				</Modal.Header>
			)}
			<Modal.Body>
				<Conditional>
					<If condition={!!contractLineItem}>
						{contractLineItem && (
							<React.Fragment>
								<ContractLineItemForm.ControlledDetail
									contractLineItem={contractLineItem}
									formStateArgs={formStateArgs}
									formNameSuffix={formNameSuffix}
									onSuccess={onClose}
									style={{ marginBottom: "2rem" }}
								/>

								<If condition={!contractLineItem.parentContractLineItem}>
									<ChildrenTable contractLineItemID={contractLineItem.id} />
								</If>
							</React.Fragment>
						)}
					</If>
					<Else>
						<span>Loading...</span>
					</Else>
				</Conditional>
			</Modal.Body>
			<Modal.Footer>
				<ContractLineItemDeleteButton instanceID={contractLineItemID} onSuccess={onClose} size="small">
					Delete Item
				</ContractLineItemDeleteButton>
			</Modal.Footer>
		</Modal>
	);
}

const childTableEditFormStateArgs = {
	editMode: EditMode.WriteOnly,
};

const childTableColumns = ["name", "amount", "edit"];

type ChildrenTableProps = {
	contractLineItemID: ContractLineItem["id"];
};

function ChildrenTable({ contractLineItemID }: ChildrenTableProps) {
	const { loading, data, error } = useChildContractLineItemListQuery({
		variables: { filters: { parentContractLineItemIDEQ: contractLineItemID } },
	});
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "contractLineItemConnection" });

	const createFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				parentContractLineItemID: contractLineItemID,
			},
		}),
		[contractLineItemID],
	);

	return (
		<Section>
			<Section.Header>
				<Heading.H3 noMargin>Discounts</Heading.H3>
			</Section.Header>
			<Section.Body>
				<Table columns={childTableColumns}>
					<Table.Header>
						<Table.Row>
							<Table.Heading column="name" noResize>
								Name
							</Table.Heading>
							<Table.Heading column="amount" noResize>
								Amount
							</Table.Heading>
							<Table.Heading column="edit" noResize></Table.Heading>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						<Conditional>
							<If condition={queryStatus === "results"}>
								{data?.contractLineItemConnection.edges.map(({ node }) => (
									<Table.Row key={node.id}>
										<Table.Cell column="name">{ContractLineItemFormat.name(node)}</Table.Cell>
										<Table.Cell column="amount">{ContractLineItemFormat.Fields.amount(node.amount)}</Table.Cell>
										<Table.Cell column="edit">
											<ControlledContractLineItemDetailModalController
												contractLineItemID={node.id}
												variant="link"
												size="small"
												formStateArgs={childTableEditFormStateArgs}
											>
												Edit
											</ControlledContractLineItemDetailModalController>
										</Table.Cell>
									</Table.Row>
								))}
							</If>
							<If condition={queryStatus === "no-results"}>
								<Table.Row>
									<Table.Cell column={childTableColumns[0]} colspan={childTableColumns.length}>
										No discounts
									</Table.Cell>
								</Table.Row>
							</If>
							<If condition={queryStatus === "loading"}>
								<Table.Row>
									<Table.Cell column={childTableColumns[0]} colspan={childTableColumns.length}>
										Loading...
									</Table.Cell>
								</Table.Row>
							</If>
							<If condition={queryStatus === "error"}>
								<Table.Row>
									<Table.Cell column={childTableColumns[0]} colspan={childTableColumns.length}>
										An error occurred
									</Table.Cell>
								</Table.Row>
							</If>
						</Conditional>

						<Table.Row>
							<Table.Cell column={childTableColumns[0]} colspan={childTableColumns.length}>
								<ChildContractLineItemCreateModalController
									variant="link"
									size="small"
									formStateArgs={createFormStateArgs}
									formNameSuffix={contractLineItemID}
								>
									Add Discount
								</ChildContractLineItemCreateModalController>
							</Table.Cell>
						</Table.Row>
					</Table.Body>
				</Table>
			</Section.Body>
		</Section>
	);
}

type ChildContractLineItemCreateModalControllerProps = Omit<ButtonProps, "onClick"> &
	Pick<ChildContractLineItemCreateModalProps, "formStateArgs" | "formNameSuffix">;

function ChildContractLineItemCreateModalController({
	formStateArgs,
	formNameSuffix,
	...props
}: ChildContractLineItemCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} {...props} />

			<Modal.If condition={isModalOpen}>
				<ChildContractLineItemCreateModal
					formStateArgs={formStateArgs}
					formNameSuffix={formNameSuffix}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type ChildContractLineItemCreateModalProps = Pick<
	ContractLineItemForm.ControlledCreateProps,
	"formStateArgs" | "formNameSuffix"
> &
	RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

function ChildContractLineItemCreateModal({
	formStateArgs,
	formNameSuffix,
	ifRef,
	onClose,
	className,
	style,
}: ChildContractLineItemCreateModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>New Discount</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<ContractLineItemForm.ControlledChildDiscountCreate
					formStateArgs={formStateArgs}
					formNameSuffix={formNameSuffix}
					onSuccess={onClose}
				/>
			</Modal.Body>
		</Modal>
	);
}
